// components/TestRunner/rules/Rule01_FiberFree_Report.js
import { RuleUtils } from "../../../utils/RuleUtils.js";

/**
 * Rule01_FiberFree_Report.js
 *
 * ✅ Règle 01 — Une fibre déclarée libre doit obligatoirement être dans l'une des 3 catégories :
 * • PV Succès
 * • PV Échec
 * • PV Hors Service
 * Type: Error
 *
 * 🧪 Tests effectués :
 * • Vérification de l'existence : fiber.etat, hasDcoPvReussite, hasPvReussite, hasDcoPvEchec, hasPvEchec, hasPvHs
 * • Contrôle RuleUtils.isFiberFree(fiber)
 * • Validation PV Succès complet : hasDcoPvReussite=true ET hasPvReussite=true
 * • Validation PV Échec complet : hasDcoPvEchec=true ET hasPvEchec=true
 * • Validation PV Hors Service : hasPvHs=true
 * • Détection conflits : plusieurs PV complets simultanés
 * • Détection configurations incomplètes : un seul flag à true par type de PV
 */

export class Rule01_FiberFree_Report {
  /**
   * Appliquer la règle 01 : PV obligatoire pour fibre libre
   * @param {Object} fiber - Objet fibre avec toutes ses données
   */
  static apply(fiber) {
    // Cas 1 : Vérifier l'existence des données nécessaires
    const missingDataError = this._checkMissingData(fiber);
    if (missingDataError) {
      this._addError(fiber, "R01", missingDataError.title, missingDataError.message);
      return;
    }

    // Vérifier si la fibre est libre
    if (!RuleUtils.isFiberFree(fiber)) {
      return; // Règle ne s'applique qu'aux fibres libres
    }

    // Analyser l'état des PV
    const pvAnalysis = this._analyzePVStatus(fiber);

    if (pvAnalysis.hasError) {
      this._addError(fiber, "R01", pvAnalysis.errorTitle, pvAnalysis.errorMessage);
    }
  }

  /**
   * Vérifier l'existence des données nécessaires
   * @param {Object} fiber - Objet fibre
   * @returns {Object|null} Erreur ou null si OK
   */
  static _checkMissingData(fiber) {
    const requiredFields = [
      { field: "etat", label: "État de la fibre" },
      { field: "hasDcoPvReussite", label: "Fibre dans le Rapport FiberCable PV Succès" },
      { field: "hasPvReussite", label: "Fichiers de mesures présents pour un PV Succès" },
      { field: "hasDcoPvEchec", label: "Fibre dans le Rapport FiberCable PV Échec" },
      { field: "hasPvEchec", label: "Fichiers de mesures présents pour un PV Échec" },
      { field: "hasPvHs", label: "Fichiers de mesures présents pour un PV Hors Service" },
    ];

    // Vérifier si au moins un champ est manquant
    const missingFields = requiredFields.filter(
      (item) => fiber[item.field] === undefined || fiber[item.field] === null
    );

    if (missingFields.length > 0) {
      return {
        title: "Impossible d'exécuter le test - Données de configuration manquantes",
        message: this._generateMissingDataMessage(fiber, requiredFields),
      };
    }

    return null;
  }

  /**
   * Analyser l'état des PV pour une fibre libre
   * @param {Object} fiber - Objet fibre
   * @returns {Object} Résultat de l'analyse
   */
  static _analyzePVStatus(fiber) {
    const analysis = {
      hasError: false,
      errorTitle: "",
      errorMessage: "",
    };

    // Vérifier les PV complets
    const hasCompleteSuccess = fiber.hasDcoPvReussite === true && fiber.hasPvReussite === true;
    const hasCompleteFailure = fiber.hasDcoPvEchec === true && fiber.hasPvEchec === true;
    const hasOutOfService = fiber.hasPvHs === true;

    // Compter les PV complets
    const completePVCount = [hasCompleteSuccess, hasCompleteFailure, hasOutOfService].filter(Boolean).length;

    // Cas 2c : Mélanges non autorisés
    if (completePVCount > 1) {
      analysis.hasError = true;
      analysis.errorTitle = "Conflit de PV détecté - Plusieurs types de PV associés simultanément";
      analysis.errorMessage = this._generateConflictMessage(fiber);
      return analysis;
    }

    // Cas 2a : PV Réussite incomplet
    if (
      (fiber.hasDcoPvReussite === true && fiber.hasPvReussite === false) ||
      (fiber.hasDcoPvReussite === false && fiber.hasPvReussite === true)
    ) {
      analysis.hasError = true;
      analysis.errorTitle = "PV Réussite incomplet - Configuration partielle détectée";
      analysis.errorMessage = this._generateIncompleteSuccessMessage(fiber);
      return analysis;
    }

    // Cas 2b : PV Échec incomplet
    if (
      (fiber.hasDcoPvEchec === true && fiber.hasPvEchec === false) ||
      (fiber.hasDcoPvEchec === false && fiber.hasPvEchec === true)
    ) {
      analysis.hasError = true;
      analysis.errorTitle = "PV Échec incomplet - Configuration partielle détectée";
      analysis.errorMessage = this._generateIncompleteFailureMessage(fiber);
      return analysis;
    }

    // Cas 2d : Aucun PV complet
    if (completePVCount === 0) {
      analysis.hasError = true;
      analysis.errorTitle = "Fibre libre sans PV associé";
      analysis.errorMessage = this._generateNoPVMessage(fiber);
      return analysis;
    }

    return analysis;
  }

  /**
   * Générer le message pour données manquantes
   * @param {Object} fiber - Objet fibre
   * @param {Array} requiredFields - Liste des champs requis
   * @returns {string} Message HTML formaté
   */
  static _generateMissingDataMessage(fiber, requiredFields) {
    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `Impossible d'exécuter le test - Données de configuration manquantes<br><br>`;

    message += `<strong>Détail des flags manquants :</strong><br>`;
    requiredFields.forEach((item) => {
      const value = fiber[item.field];
      const status = value !== undefined && value !== null ? "✅" : "❌";
      message += `- ${item.label} : ${status}<br>`;
    });

    message += `<br><strong>⚡ Action requise :</strong><br>`;
    message += `- Vérifier la déclaration de la fibre dans la Configuration des Tiroirs<br>`;
    message += `- Vérifier la présence dans un rapport fiber cable<br>`;
    message += `- Vérifier la présence de fichiers de mesures PV Réussite, PV Échec, PV Hors Service`;

    return message;
  }

  /**
   * Générer le message pour PV Réussite incomplet
   * @param {Object} fiber - Objet fibre
   * @returns {string} Message HTML formaté
   */
  static _generateIncompleteSuccessMessage(fiber) {
    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `PV Réussite incomplet - Configuration partielle détectée<br><br>`;

    message += `<strong>Détail du problème :</strong><br>`;
    message += `- Fibre dans le Rapport FiberCable PV Succès : ${fiber.hasDcoPvReussite ? "✅" : "❌"}<br>`;
    message += `- Fichiers de mesures présents pour un PV Succès : ${fiber.hasPvReussite ? "✅" : "❌"}<br><br>`;

    message += `<strong>⚡ Action requise :</strong><br>`;
    message += `- Vérifier la cohérence d'une mesure avec un PV Succès`;

    return message;
  }

  /**
   * Générer le message pour PV Échec incomplet
   * @param {Object} fiber - Objet fibre
   * @returns {string} Message HTML formaté
   */
  static _generateIncompleteFailureMessage(fiber) {
    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `PV Échec incomplet - Configuration partielle détectée<br><br>`;

    message += `<strong>Détail du problème :</strong><br>`;
    message += `- Fibre dans le Rapport FiberCable PV Échec : ${fiber.hasDcoPvEchec ? "✅" : "❌"}<br>`;
    message += `- Fichiers de mesures présents pour un PV Échec : ${fiber.hasPvEchec ? "✅" : "❌"}<br><br>`;

    message += `<strong>⚡ Action requise :</strong><br>`;
    message += `- Vérifier la cohérence d'une mesure avec un PV Échec`;

    return message;
  }

  /**
   * Générer le message pour conflit de PV
   * @param {Object} fiber - Objet fibre
   * @returns {string} Message HTML formaté
   */
  static _generateConflictMessage(fiber) {
    const hasCompleteSuccess = fiber.hasDcoPvReussite === true && fiber.hasPvReussite === true;
    const hasCompleteFailure = fiber.hasDcoPvEchec === true && fiber.hasPvEchec === true;
    const hasOutOfService = fiber.hasPvHs === true;

    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `Conflit de PV détecté - Plusieurs types de PV associés simultanément<br><br>`;

    message += `<strong>Détail du problème :</strong><br>`;
    message += `- PV Réussite complet : ${hasCompleteSuccess ? "✅" : "❌"}<br>`;
    message += `- PV Échec complet : ${hasCompleteFailure ? "✅" : "❌"}<br>`;
    message += `- PV Hors Service : ${hasOutOfService ? "✅" : "❌"}<br><br>`;

    message += `<strong>⚡ Action requise :</strong><br>`;
    message += `- Conserver uniquement le PV correct et supprimer les autres`;

    return message;
  }

  /**
   * Générer le message pour aucun PV complet
   * @param {Object} fiber - Objet fibre
   * @returns {string} Message HTML formaté
   */
  static _generateNoPVMessage(fiber) {
    const hasCompleteSuccess = fiber.hasDcoPvReussite === true && fiber.hasPvReussite === true;
    const hasCompleteFailure = fiber.hasDcoPvEchec === true && fiber.hasPvEchec === true;
    const hasOutOfService = fiber.hasPvHs === true;

    let message = `<strong>⚠️ Problème détecté :</strong><br>`;
    message += `Fibre déclarée comme libre mais sans fichier de mesure .sor correspondant.<br><br>`;

    message += `<strong>Détail du problème :</strong><br>`;
    message += `- PV Réussite complet : ${hasCompleteSuccess ? "✅" : "❌"}<br>`;
    message += `- PV Échec complet : ${hasCompleteFailure ? "✅" : "❌"}<br>`;
    message += `- PV Hors Service : ${hasOutOfService ? "✅" : "❌"}<br><br>`;

    message += `<strong>⚡ Action requise :</strong><br>`;
    message += `- Associer un PV (Réussite, Échec ou Hors Service) à cette fibre<br>`;
    message += `- Effectuer les mesures nécessaires si la fibre est réellement libre`;

    return message;
  }

  /**
   * Ajouter une erreur au résultat d'une fibre
   */
  static _addError(fiber, rule, title, message) {
    // Initialize testResults if it doesn't exist
    if (!fiber.testResults) {
      fiber.testResults = {
        status: "success",
        hasError: false,
        messages: [],
      };
    }

    // Configure error details
    fiber.testResults.status = "error";
    fiber.testResults.hasError = true;
    fiber.testResults.messages.push({
      type: "error",
      rule,
      title,
      message,
    });
  }
}
